import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Star, Clock, MapPin, Users, Check } from 'lucide-react';
import { formatPrice, getCategoryColor, getCategoryLabel } from '../../lib/utils';
import { useSettings } from '../../contexts/SettingsContext';

export const HorizontalTourCard = ({ tour, listingSettings = {} }) => {
  const { settings } = useSettings();
  
  // Merge passed listingSettings with defaults
  const ls = {
    showPrices: true,
    showRatings: true,
    showBadges: true,
    cardBorderRadius: 12,
    cardShadow: 'medium',
    priceColor: '#22c55e',
    badgePosition: 'top-left',
    hoverEffect: 'scale',
    cardSize: 'standard',
    ...listingSettings
  };
  
  // Check if all badges should be hidden
  const hideAllBadges = tour.hide_all_badges === true || !ls.showBadges;
  
  // Get custom badges grouped by position
  const customBadges = tour.custom_badges || [];
  const getBadgesByPosition = (position) => customBadges.filter(b => b.position === position);
  
  // Position classes for badges - use listingSettings.badgePosition
  const positionClasses = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2',
  };
  
  // Dynamic shadow class
  const shadowClass = 
    ls.cardShadow === 'none' ? '' :
    ls.cardShadow === 'small' ? 'shadow-sm' :
    ls.cardShadow === 'large' ? 'shadow-lg' : 'shadow-md';
  
  // Dynamic hover effect class
  const hoverClass = 
    ls.hoverEffect === 'none' ? '' :
    ls.hoverEffect === 'lift' ? 'hover:shadow-xl hover:-translate-y-1' :
    ls.hoverEffect === 'glow' ? 'hover:ring-2 hover:ring-primary/50' : 'hover:scale-[1.02]';
  
  // Card height based on size
  const cardMinHeight = 
    ls.cardSize === 'compact' ? '140px' :
    ls.cardSize === 'large' ? '220px' : '170px';
  
  return (
    <Link to={`/tours/${tour.id}`} data-testid={`tour-card-${tour.id}`}>
      <Card 
        className={`group overflow-hidden bg-white border border-border/40 h-full transition-all duration-300 ${shadowClass} ${hoverClass}`}
        style={{ borderRadius: `${ls.cardBorderRadius}px` }}
      >
        <div className="flex flex-row h-full">
          {/* Image Section - Left Side */}
          <div 
            className="relative w-[38%] overflow-hidden flex-shrink-0"
            style={{ minHeight: cardMinHeight }}
          >
            <img
              src={tour.image_url}
              alt={tour.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/5" />
            
            {/* Category Badge - Top Left (if no custom badge at top-left) */}
            {!hideAllBadges && tour.show_category_badge !== false && getBadgesByPosition('top-left').length === 0 && (
              <Badge 
                className="absolute top-2 left-2 text-white border-0 text-[10px] font-semibold shadow-md px-2 py-0.5"
                style={{ backgroundColor: tour.category_badge_color || getCategoryColor(tour.category).replace('bg-', '#') }}
                data-testid={`tour-category-${tour.id}`}
              >
                {tour.category_badge_text || getCategoryLabel(tour.category)}
              </Badge>
            )}

            {/* Custom Badges by Position */}
            {!hideAllBadges && Object.entries(positionClasses).map(([position, classes]) => {
              const badges = getBadgesByPosition(position);
              // For top-left, if category badge is shown, custom badges come after it
              const showCategoryFirst = position === 'top-left' && tour.show_category_badge !== false;
              
              if (badges.length === 0 && !showCategoryFirst) return null;
              
              return (
                <div key={position} className={`absolute ${classes} flex flex-col gap-1`}>
                  {showCategoryFirst && tour.show_category_badge !== false && (
                    <Badge 
                      className="text-white border-0 text-[10px] font-semibold shadow-md px-2 py-0.5"
                      style={{ backgroundColor: tour.category_badge_color || '#22c55e' }}
                      data-testid={`tour-category-${tour.id}`}
                    >
                      {tour.category_badge_text || getCategoryLabel(tour.category)}
                    </Badge>
                  )}
                  {badges.map((badge, idx) => (
                    <Badge 
                      key={idx}
                      className="text-white border-0 text-[10px] font-semibold shadow-md px-2 py-0.5"
                      style={{ backgroundColor: badge.color || '#ef4444' }}
                    >
                      {badge.text}
                    </Badge>
                  ))}
                </div>
              );
            })}

            {/* Discount Badge */}
            {tour.discount_percentage > 0 && getBadgesByPosition('bottom-left').length === 0 && (
              <Badge className="absolute bottom-2 left-2 bg-red-500 text-white border-0 text-[10px] font-bold shadow-md">
                -{tour.discount_percentage}%
              </Badge>
            )}
          </div>

          {/* Details Section - Right Side */}
          <div className="flex-1 p-3 flex flex-col justify-between">
            {/* Top: Title and Info */}
            <div className="space-y-1.5">
              {/* Title - Bigger & Clearer */}
              <h3 
                className={`font-bold text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors tracking-tight ${
                  settings.card_title_size === 'base' ? 'text-base sm:text-lg' :
                  settings.card_title_size === 'xl' ? 'text-xl sm:text-2xl' :
                  settings.card_title_size === '2xl' ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'
                }`}
                style={{ fontFamily: settings.card_title_font || 'inherit' }}
              >
                {tour.title}
              </h3>
              
              {/* Rating */}
              {ls.showRatings && (
                <div className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <span className="font-bold text-foreground text-sm">{tour.rating}</span>
                  <span className="text-muted-foreground text-xs">({tour.reviews_count} reviews)</span>
                </div>
              )}

              {/* Quick Info Pills - Duration and Free Cancellation */}
              <div className="flex flex-wrap gap-1.5">
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground bg-slate-100 px-2 py-1 rounded-full">
                  <Clock className="h-3 w-3" />
                  <span>{tour.duration}</span>
                </div>
                {tour.show_cancellation_badge !== false && (
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground bg-slate-100 px-2 py-1 rounded-full">
                    <Check className="h-3 w-3" />
                    <span>{tour.cancellation_text || 'Free Cancellation'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom: Price Section */}
            <div className="flex items-end justify-between pt-2 mt-2 border-t border-slate-100">
              <div>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">From</span>
                <div className="flex items-baseline gap-1">
                  {tour.original_price && tour.discount_percentage > 0 && (
                    <span className="text-xs text-muted-foreground line-through">
                      {formatPrice(tour.original_price)}
                    </span>
                  )}
                  {ls.showPrices && (
                    <span 
                      className="text-lg font-bold"
                      style={{ color: ls.priceColor }}
                    >
                      {formatPrice(tour.price)}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-xs font-semibold text-primary group-hover:translate-x-0.5 transition-transform">
                Details →
              </span>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default HorizontalTourCard;
